import { useMemo, useState } from 'react'
import {
  CheckIcon,
  ChildrenIcon,
  DataSurface,
  Field,
  InfoIcon,
  Note,
  PageHead,
  PrivacyIcon,
  Select,
  StatCard,
  StatGrid,
  StatusPill,
  TableCard,
  Td,
  Thead,
  Toolbar,
  Tr,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import { getClassConsent, type ConsentClassRow } from '../../../shared/api/rights'
import type { ParentAuth } from '../../../shared/api/parent'
import {
  consentHeadline,
  orderConsentRows,
  summariseConsent,
  type ConsentSort,
} from './consentRows'
import {
  SCHOOL_COPY,
  SORT_LABEL,
  describeError,
  headlineText,
  statusLabel,
  statusTone,
  type Lang,
} from './copy'

/**
 * S3 — the school's consent dashboard.
 *
 * ⚠️ **THE LAYOUT IS INVENTED.** settings-and-controls.md §4 gives S3 a single
 * line — *"which children have consent, which are pending, which revoked"* —
 * and `wireframes.md`'s C-section stops at C5, so no screen exists to follow.
 * The precedent for inventing it and saying so is OD-55. 📋 Reported.
 *
 * 🔴 IT STILL 401s TODAY, and says so by name. Parent login exists; **staff
 * login does not** (OD-51). The teacher tier already made that mistake legible
 * rather than silent, and this screen inherits the discipline.
 */

export interface ConsentDashboardProps {
  classId: string
  lang?: Lang
  auth?: ParentAuth
}

export function ConsentDashboard({ classId, lang = 'en', auth }: ConsentDashboardProps) {
  const t = SCHOOL_COPY[lang]
  const [sort, setSort] = useState<ConsentSort>('status')

  const resource = useResource<ConsentClassRow[]>(
    (signal) => getClassConsent(classId, { ...auth, signal }),
    [classId],
    /*
     * ⚠️ EMPTY MEANS "NO CHILDREN", NOT "NO CONSENT". A class of twenty
     * children with zero consent rows is a full table of PENDING, which is the
     * state this screen exists to make visible — collapsing it into the empty
     * state would hide exactly the thing the school is accountable for.
     */
    { isEmpty: (rows) => rows.length === 0 },
  )

  const rows = useMemo(
    () => orderConsentRows(resource.data ?? [], sort),
    [resource.data, sort],
  )
  const summary = useMemo(() => summariseConsent(resource.data ?? []), [resource.data])

  return (
    <section className="font-dash text-dash-sm text-ink">
      <PageHead title={t.consentTitle} subtitle={t.consentSubtitle} />

      <div>
        <DataSurface
          state={resource.state}
          skeletonRows={6}
          emptyMessage={t.headlineNobodyEnrolled}
          errorMessage={describeError(resource.error, lang)}
          /* 🔴 "When it last worked" — the clause DataSurface says gets
             dropped, and the one that tells an admin whether to panic. */
          errorLastWorked={
            resource.lastLoadedAt
              ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
              : t.neverWorked
          }
          errorRetryLabel={t.retry}
          onRetry={resource.retry}
        >
          {resource.data === null ? null : (
            <>
              {/* 🔴 THE HEADLINE IS THE POINT OF THE SCREEN. On the day this
                  ships every child in every school is PENDING — OD-50 kept the
                  consent row unwritten until §9.4. A wall of red with no
                  sentence reads as a data-loss incident, and the school's first
                  call is to us rather than to the parents. */}
              <Note
                tone={summary.pending > 0 || summary.expired > 0 ? 'neutral' : 'success'}
                icon={
                  summary.pending > 0 || summary.expired > 0 ? (
                    <InfoIcon size={16} />
                  ) : (
                    <CheckIcon size={16} />
                  )
                }
                detail={summary.pending > 0 ? t.pendingConsequence : undefined}
              >
                {headlineText(consentHeadline(summary), lang)}
              </Note>

              {/*
                ✅ FIVE EQUAL CARDS. The consent counts are one quantity split
                five ways, and sizing each box to its own label made "Children"
                twice the width of "Expired" — so the four numbers a school is
                accountable for did not line up as a row that could be read at
                once.
              */}
              <div className="mt-6">
                <StatGrid>
                  <StatCard
                    label={t.children}
                    value={summary.total}
                    icon={<ChildrenIcon size={18} />}
                    tone="primary"
                  />
                  <StatCard
                    label={t.granted}
                    value={summary.granted}
                    icon={<CheckIcon size={18} />}
                    tone="leaf"
                  />
                  <StatCard
                    label={t.pending}
                    value={summary.pending}
                    icon={<InfoIcon size={18} />}
                    tone="sunshine"
                  />
                  <StatCard
                    label={t.expired}
                    value={summary.expired}
                    icon={<PrivacyIcon size={18} />}
                    tone="indigo"
                  />
                  <StatCard
                    label={t.revoked}
                    value={summary.revoked}
                    icon={<PrivacyIcon size={18} />}
                    tone="coral"
                  />
                </StatGrid>
              </div>

              <div className="mt-6">
                <Toolbar>
                  <Field label={t.sortLabel} htmlFor="consent-sort">
                    <Select
                      id="consent-sort"
                      value={sort}
                      onChange={(e) => setSort(e.target.value as ConsentSort)}
                    >
                      {(Object.keys(SORT_LABEL) as ConsentSort[]).map((option) => (
                        <option key={option} value={option}>
                          {SORT_LABEL[option][lang]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </Toolbar>
              </div>

              {/* 🔴 SORTS, NEVER FILTERS. An admin working down a filtered list
                  cannot see which parents they have already called. */}
              <TableCard>
                <table className="w-full border-collapse">
                  <Thead headers={[t.roll, t.student, t.status, t.givenOn, t.keptUntil]} />
                  <tbody>
                    {rows.map((r) => (
                      <Tr key={r.playerId}>
                        <Td num bold>
                          {r.rollNumber ?? t.dash}
                        </Td>
                        <Td>{r.name}</Td>
                        <Td>
                          <StatusPill tone={statusTone(r.status)} icon={<PrivacyIcon size={14} />}>
                            {statusLabel(r.status, lang)}
                          </StatusPill>
                        </Td>
                        <Td soft>
                          {r.grantedAt ? new Date(r.grantedAt).toLocaleDateString() : t.dash}
                        </Td>
                        <Td soft>
                          {r.retainUntil ? new Date(r.retainUntil).toLocaleDateString() : t.dash}
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            </>
          )}
        </DataSurface>
      </div>
    </section>
  )
}
